// client/src/components/influencer/InfluencerSNSAdd.js
// SNS 계정 추가 — A안 리디자인: 진행 안내 + 폼 2열 (검증·일일 제한·제출·복구 로직은 기존 그대로)
// 실사양 유지: 4개 플랫폼 동시 입력 + 증빙 파일 첨부
import React, { useMemo, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

const PLATFORM_FIELDS = [
  { key: 'instagram', label: '인스타그램', icon: '/images/sns/instagram.svg', placeholder: '@계정명 입력' },
  { key: 'blog', label: '블로그', icon: '/images/sns/blog.svg', placeholder: '블로그명 입력' },
  { key: 'youtube', label: '유튜브', icon: '/images/sns/youtube.svg', placeholder: '@채널명 입력' },
  { key: 'tiktok', label: '틱톡', icon: '/images/sns/tiktok.svg', placeholder: '@계정명 입력' },
];

function InfluencerSNSAdd() {
  const [values, setValues] = useState({ instagram: '', blog: '', youtube: '', tiktok: '' });
  const setValue = (key, v) => setValues(prev => ({ ...prev, [key]: v }));

  const [files, setFiles] = useState([]); // File[]
  const fileInputRef = useRef(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [limitMsg, setLimitMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const lastDraftRef = useRef(null);
  const submittingRef = useRef(false);

  const anyFilled = useMemo(() => {
    return Object.values(values).some(v => (v || '').trim().length > 0);
  }, [values]);

  const userKey = (sessionStorage.getItem('current_user_key') || '').toLowerCase();
  const dayKey = (() => {
    const d = new Date(); const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,'0'); const dd = String(d.getDate()).padStart(2,'0');
    return `sns_add_daily:${userKey}:${y}${m}${dd}`;
  })();
  const getDaily = () => {
    try { return Number(localStorage.getItem(dayKey) || 0); } catch { return 0; }
  };
  const incDaily = () => {
    try {
      const n = getDaily() + 1;
      localStorage.setItem(dayKey, String(n));
    } catch {}
  };

  const onPickFiles = () => fileInputRef.current?.click();
  const onFilesSelected = (e) => {
    const accepts = new Set(['image/png','image/jpeg','image/webp','application/pdf']);
    const MAX_BYTES = 10 * 1024 * 1024; // 10MB
    const picked = Array.from(e.target.files || []);
    const next = [...files];
    let rejected = 0;

    for (const f of picked) {
      if (!accepts.has(f.type)) continue;
      if (f.size > MAX_BYTES) { rejected++; continue; }
      next.push(f);
    }
    setFiles(next);
    e.target.value = '';

    if (rejected > 0) {
      setLimitMsg(`파일당 최대 10MB까지 업로드 가능합니다. 초과 ${rejected}개 파일은 제외했어요.`);
      setLimitOpen(true);
    }
  };
  const removeFileAt = (idx) => {
    const next = files.slice();
    next.splice(idx, 1);
    setFiles(next);
  };

  const restoreDraft = () => {
    const snap = lastDraftRef.current || {};
    setValues({
      instagram: snap.instagram || '',
      blog: snap.blog || '',
      youtube: snap.youtube || '',
      tiktok: snap.tiktok || '',
    });
    setFiles(snap.files || []);
  };

  const submit = async () => {
    if (!anyFilled) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    setConfirmOpen(false);

    lastDraftRef.current = { ...values, files };
    setDoneOpen(true);
    setValues({ instagram: '', blog: '', youtube: '', tiktok: '' });
    setFiles([]);

    setLoading(true);
    try {
      const fd = new FormData();
      const snap = lastDraftRef.current || {};
      fd.append('instagram', String(snap.instagram || '').trim());
      fd.append('blog',      String(snap.blog      || '').trim());
      fd.append('youtube',   String(snap.youtube   || '').trim());
      fd.append('tiktok',    String(snap.tiktok    || '').trim());
      (snap.files || []).forEach(f => fd.append('files', f));

      const res = await fetch(`${API_URL}/api/influencer/mybiz/sns-add`, {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data?.ok) {
        incDaily();
      } else if (res.status === 429) {
        setDoneOpen(false);
        restoreDraft();
        setLimitMsg(data?.message || '하루에 5번까지만 추가할 수 있습니다.');
        setLimitOpen(true);
      } else if (res.status === 413) {
        setDoneOpen(false);
        restoreDraft();
        setLimitMsg('파일당 최대 10MB까지 업로드 가능합니다.');
        setLimitOpen(true);
      } else {
        setDoneOpen(false);
        restoreDraft();
        alert(data?.message || '추가 등록 요청 실패');
      }
    } catch {
      setDoneOpen(false);
      restoreDraft();
      alert('요청 실패');
    }
    setLoading(false);
    submittingRef.current = false;
  };

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">SNS 계정 추가</h2>
        <p className="mb-page-description">계정 이름과 본인 증빙을 보내면 관리자 확인 후 등록돼요.</p>
      </div>

      <div className="mb-grid-2" style={{ alignItems: 'start' }}>
        {/* 왼쪽: 진행 안내 */}
        <div className="mb-card" style={{ margin: 0 }}>
          <h3 className="mb-section-title">이렇게 진행돼요</h3>
          <div className="checkline">
            <i>1</i>
            <div>
              <b>계정 이름 입력</b>
              <span>추가할 플랫폼 칸에 계정명을 입력해요 (여러 개 동시 가능)</span>
            </div>
          </div>
          <div className="checkline">
            <i>2</i>
            <div>
              <b>본인 증빙 첨부</b>
              <span>계정 관리 화면 캡처 등 내 계정임을 확인할 수 있는 이미지</span>
            </div>
          </div>
          <div className="checkline">
            <i>3</i>
            <div>
              <b>관리자 확인 후 등록</b>
              <span>운영팀 확인 후 영업일 2일 이내에 등록돼요</span>
            </div>
          </div>
          <div className="note note--amber">추가 요청은 하루 5회까지 보낼 수 있어요.</div>
        </div>

        {/* 오른쪽: 폼 */}
        <div className="mb-card" style={{ margin: 0 }}>
          {PLATFORM_FIELDS.map(({ key, label, icon, placeholder }) => (
            <div className="mb-form-group" key={key}>
              <label className="mb-form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <img src={icon} alt="" width="18" height="18" onError={e => { e.currentTarget.style.display = 'none'; }} />
                {label}
              </label>
              <input
                className="mb-form-control"
                value={values[key]}
                onChange={e => setValue(key, e.target.value)}
                placeholder={placeholder}
                disabled={loading}
              />
            </div>
          ))}

          <div className="mb-form-group">
            <label className="mb-form-label">본인 증빙 이미지</label>
            {files.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {files.map((f, i) => (
                  <span key={i} className="file-chip">
                    <span>{f.name}</span>
                    <button type="button" onClick={() => removeFileAt(i)} disabled={loading} aria-label="파일 제거">×</button>
                  </span>
                ))}
              </div>
            )}
            <button type="button" className="upload" onClick={onPickFiles} disabled={loading}>
              <b>파일을 눌러서 선택</b>
              JPG·PNG·WEBP·PDF · 파일당 10MB 이하 · 계정 관리 화면 캡처 등
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              style={{ display: 'none' }}
              onChange={onFilesSelected}
              disabled={loading}
            />
          </div>

          <button
            type="button"
            className="mb-btn mb-btn-primary mb-btn-lg mb-btn-full"
            disabled={!anyFilled || loading}
            onClick={() => {
              if (getDaily() >= 5) { setLimitMsg('하루에 5번까지만 추가할 수 있습니다.'); setLimitOpen(true); return; }
              setConfirmOpen(true);
            }}
          >
            {loading ? '처리 중...' : '계정 추가 요청하기'}
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="mbm-overlay">
          <div className="mb-card mbm" style={{ width: 400, maxWidth: '92vw', margin: 0, textAlign: 'center', padding: '32px' }}>
            <p style={{ margin: 0, fontSize: 15.5, color: '#0b1324', lineHeight: 1.6 }}>
              운영팀 확인 후, 영업일 2일 이내로 추가됩니다. 추가 등록하시겠습니까?
            </p>
            <div className="mbm-foot">
              <button className="mb-btn mb-btn-secondary" onClick={() => setConfirmOpen(false)}>취소</button>
              <button className="mb-btn mb-btn-primary" onClick={submit} disabled={loading}>확인</button>
            </div>
          </div>
        </div>
      )}

      {doneOpen && (
        <div className="mbm-overlay">
          <div className="mb-card mbm" style={{ width: 400, maxWidth: '92vw', margin: 0, textAlign: 'center', padding: '32px' }}>
            <p style={{ margin: 0, fontSize: 15.5, color: '#0b1324', lineHeight: 1.6 }}>
              SNS 계정 추가 등록 요청이 완료되었습니다!<br />
              운영팀에서 확인 후 직접 추가합니다. (영업일 2일 이내)
            </p>
            <div className="mbm-foot">
              <button className="mb-btn mb-btn-primary" onClick={() => setDoneOpen(false)}>확인</button>
            </div>
          </div>
        </div>
      )}

      {limitOpen && (
        <div className="mbm-overlay">
          <div className="mb-card mbm" style={{ width: 400, maxWidth: '92vw', margin: 0, textAlign: 'center', padding: '32px' }}>
            <p style={{ margin: 0, fontSize: 15.5, color: '#0b1324', lineHeight: 1.6 }}>
              {limitMsg || '하루에 5번까지만 추가할 수 있습니다.'}
            </p>
            <div className="mbm-foot">
              <button className="mb-btn mb-btn-primary" onClick={() => setLimitOpen(false)}>확인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InfluencerSNSAdd;
